/**
 * Custom hook wrapping the auth store for convenient component use.
 *
 * Returns:
 *   - user: UserProfile | null
 *   - isAuthenticated: boolean
 *   - isLoading: boolean
 *   - error: string | null
 *   - login(email, password): Promise<void>
 *   - logout(): Promise<void>
 *
 * Redirect behaviour:
 *   - If isAuthenticated is false and isLoading is false, components using
 *     requireAuth() will navigate to /login automatically.
 *
 * requireAuth():
 *   - Call inside protected route components. Triggers redirect to /login
 *     if the user is not authenticated after the initial load completes.
 *   - Also handles the case where loadUser() hasn't resolved yet (isLoading=true).
 *
 * Example usage:
 *   const { user, logout } = useAuth();
 *   const { requireAuth } = useAuth();
 *   requireAuth(); // at top of a protected page component
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';

export function useAuth() {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const error = useAuthStore((s) => s.error);
  const login = useAuthStore((s) => s.login);
  const logout = useAuthStore((s) => s.logout);
  const loadUser = useAuthStore((s) => s.loadUser);
  const clearError = useAuthStore((s) => s.clearError);

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    loadUser,
    clearError,
  };
}

/**
 * useRequireAuth — lightweight hook for protected routes that ONLY redirects.
 * Does not return any auth data (use useAuth() for that).
 * Useful in layout wrappers that protect entire route trees.
 *
 * Example:
 *   function ProtectedLayout() {
 *     useRequireAuth();
 *     return <Outlet />;
 *   }
 */
export function useRequireAuth(): void {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]);
}
